 
import { DEVELOPMENT_SOURCE_METRIC } from 'constants/sources';
import assertNever from 'helpers/assertNever';
import {
    BOE_PROPERTY_TYPES,
    COST_BASIS_OPTIONS,
    COST_BASIS_TYPES,
    DATA_CENTER_COST_BASIS_OPTIONS,
    DATA_CENTER_HARD_SOFT_COSTS_OPTIONS,
    EXPENSE_CALCULATION_MODE,
    HARD_SOFT_COSTS_OPTIONS,
    STUDENT_HOUSING_COST_BASIS_OPTIONS,
    STUDENT_HOUSING_HARD_SOFT_COSTS_OPTIONS,
    UNIT_OF_TIME_TYPES,
} from './constants';
import { BOESlideoutFormValues, IBackOfEnvelopePayload, PropertySizeView } from './types';
import { ProFormaModelIds } from 'constants/proForma';
import { BOEScenario } from '../../types';
import { isApartmentWithRetailModel, isStudentHousingModel } from 'helpers/proForma';
import { IOptionValue } from 'bricks/types';
import { numeralFormatterCurrency } from 'ns_libs/formatter';

export const getValueOrNull = <T>(value: T | null | undefined): T | null => (value !== null && value !== undefined ? value : null);

export const prepareBackOfEnvelopePayload = (formikValues: BOESlideoutFormValues): IBackOfEnvelopePayload => {
    const normalizeValue = (value: number | null | undefined): number | undefined => (value !== null && value !== undefined ? value : undefined);
    const isApartmentWithRetail = Number(formikValues.propertyType.value) === ProFormaModelIds.APARTMENT_WITH_RETAIL;
    const isStudentHousing = isStudentHousingModel(Number(formikValues.propertyType.value) as ProFormaModelIds);

    const loanCalculationField = (() => {
        switch (formikValues.selectedLoanCalculationOptions?.value) {
            case DEVELOPMENT_SOURCE_METRIC.LTC:
                return { loanToCost: formikValues.contructionFinancing ? formikValues.contructionFinancing / 100 : undefined };
            case DEVELOPMENT_SOURCE_METRIC.LTV:
                return { loanToValue: formikValues.contructionFinancing ? formikValues.contructionFinancing / 100 : undefined };
            case DEVELOPMENT_SOURCE_METRIC.DEBT_YIELD:
                return { debtYield: formikValues.contructionFinancing ? formikValues.contructionFinancing / 100 : undefined };
            default:
                return {};
        }
    })();

    const expensePercentOfGpr = (() => {
        switch (formikValues.expenseCalculationMode) {
            case EXPENSE_CALCULATION_MODE.PERCENT_OF_GPR:
                return formikValues.expensePercentOfGpr ? formikValues.expensePercentOfGpr / 100 : undefined;
            case EXPENSE_CALCULATION_MODE.UNIT_COST:
                return 0;
            default:
                return assertNever(formikValues.expenseCalculationMode);
        }
    })();

    const retailExpensePercentOfGpr = (() => {
        if (!isApartmentWithRetail) return undefined;
        switch (formikValues.retailExpenseCalculationMode) {
            case EXPENSE_CALCULATION_MODE.PERCENT_OF_GPR:
                return formikValues.retailExpensePercentOfGpr ? formikValues.retailExpensePercentOfGpr / 100 : undefined;
            case EXPENSE_CALCULATION_MODE.UNIT_COST:
                return 0;
            case undefined:
                return undefined;
            default:
                return assertNever(formikValues.retailExpenseCalculationMode);
        }
    })();

    const retailFields = isApartmentWithRetail ? {
        retailBuiltSf: normalizeValue(formikValues.retailGrossSquareFootage),
        retailBuildingEfficiency: formikValues.retailEfficiency ? formikValues.retailEfficiency / 100 : undefined,
        retailExpenseCostPerSf: formikValues.retailExpenseCostSubdiv === COST_BASIS_TYPES.PER_SF ? normalizeValue(formikValues.retailExpenses) : undefined,
        retailExpenseCostTime: formikValues.retailExpenseCostTime,
        retailExpenseCostSubdiv: formikValues.retailExpenseCostSubdiv,
        retailExpenseCalculationMode: formikValues.retailExpenseCalculationMode,
        retailRentPricePerSf: normalizeValue(formikValues.retailRentPrice),
        retailRentPriceTime: formikValues.retailRentPriceTime,
        retailNumberOfSuites: normalizeValue(formikValues.retailNumberOfSuites),
        retailExpensePercentOfGpr,
    } : {};

    const studentHousingFields = isStudentHousing ? {
        bedsPerUnit: normalizeValue(formikValues.bedsPerUnit),
        monthlyRentPricePerBed: formikValues.rentPriceSubdiv === COST_BASIS_TYPES.PER_BED
            ? normalizeValue(formikValues.rentPrice)
            : undefined,
        monthlyExpenseCostPerBed: formikValues.expenseCostSubdiv === COST_BASIS_TYPES.PER_BED
            ? normalizeValue(formikValues.expenses)
            : undefined,
    } : {};

    return {
        name: formikValues.name,
        dealId: formikValues.dealId,
        propertyTypeId: formikValues.propertyType.value,
        builtSf: formikValues.grossSquareFootage!,
        buildingEfficiency: formikValues.efficiency ? formikValues.efficiency / 100 : undefined,
        monthlyRentPricePerUnit:
            (
                formikValues.rentPriceSubdiv === COST_BASIS_TYPES.PER_UNIT ||
                formikValues.rentPriceSubdiv === COST_BASIS_TYPES.PER_KW ||
                formikValues.rentPriceSubdiv === COST_BASIS_TYPES.PER_MW
            )
                ? normalizeValue(formikValues.rentPrice)
                : undefined,
        monthlyRentPricePerSf: formikValues.rentPriceSubdiv === COST_BASIS_TYPES.PER_SF ? normalizeValue(formikValues.rentPrice) : undefined,
        rentPriceTime: formikValues.rentPriceTime,
        rentPriceSubdiv: formikValues.rentPriceSubdiv,
        numberOfUnits: formikValues.units!,
        monthlyExpenseCostPerUnit:
            (
                formikValues.expenseCostSubdiv === COST_BASIS_TYPES.PER_UNIT ||
                formikValues.expenseCostSubdiv === COST_BASIS_TYPES.PER_KW ||
                formikValues.expenseCostSubdiv === COST_BASIS_TYPES.PER_MW
            )
                ? normalizeValue(formikValues.expenses)
                : undefined,
        monthlyExpenseCostPerSf: formikValues.expenseCostSubdiv === COST_BASIS_TYPES.PER_SF ? normalizeValue(formikValues.expenses) : undefined,
        expenseCostTime: formikValues.expenseCostTime,
        expenseCostSubdiv: formikValues.expenseCostSubdiv,
        expenseCalculationMode: formikValues.expenseCalculationMode,
        expensePercentOfGpr,
        assumedExitCapRate: formikValues.exitCapRate ? formikValues.exitCapRate / 100 : undefined,
        marketCapRate: formikValues.marketCapRate ? formikValues.marketCapRate / 100 : undefined,
        acquisition: formikValues.acquisitionCosts!,
        hardCosts: formikValues.hardCosts ? normalizeValue(formikValues.hardCosts) : undefined,
        hardCostsSubdiv: formikValues.hardCostsSubdiv,
        softCosts: formikValues.softCosts ? normalizeValue(formikValues.softCosts) : undefined,
        softCostsSubdiv: formikValues.softCostsSubdiv,
        developerFeeRate: formikValues.developerFee ? formikValues.developerFee / 100 : undefined,
        annualPercentageRate: formikValues.apr ? formikValues.apr / 100 : undefined,
        averageLoanBalance: formikValues.avgLoanBalance ? formikValues.avgLoanBalance / 100 : undefined,
        loanTermMonths: formikValues.loanTerm!,
        loanTermTime: formikValues.loanTermTime, // Backend is multiplying it
        ...retailFields,
        ...studentHousingFields,
        ...loanCalculationField,
    };
};

export const calculateBOEValues = (values: BOESlideoutFormValues) => {
    const isPerUnitType = (subdiv: string) => subdiv === COST_BASIS_TYPES.PER_UNIT ||
        subdiv === COST_BASIS_TYPES.PER_KW ||
        subdiv === COST_BASIS_TYPES.PER_MW ||
        subdiv === COST_BASIS_TYPES.PER_BED;

    // Convert number_of_units to kW for unit conversions (units is in the unit specified by rent_price_subdiv)
    const unitsInKw = (() => {
        if (values.rentPriceSubdiv === COST_BASIS_TYPES.PER_MW) {
            return (values.units ?? 0) * 1000; // Convert MW to kW
        }
        return values.units ?? 0; // Already in kW or units
    })();

    const totalBeds = (values.units ?? 0) * (values.bedsPerUnit ?? 0);

    const calculateCostWithUnitConversion = (
        amount: number | null,
        targetSubdiv: string,
        rentPriceSubdiv: string,
        units: number | null,
        defaultMultiplier: number,
        fallbackMultiplier: number,
    ) => {
        if (!isPerUnitType(targetSubdiv)) {
            // Per SF calculations
            return (amount ?? 0) * fallbackMultiplier * defaultMultiplier;
        }

        // Per unit calculations with unit conversion
        let unitsToUse = units ?? 0;

        if (targetSubdiv === COST_BASIS_TYPES.PER_BED) {
            unitsToUse = totalBeds;
        } else if (rentPriceSubdiv === COST_BASIS_TYPES.PER_MW && targetSubdiv === COST_BASIS_TYPES.PER_KW) {
            unitsToUse = unitsInKw; // Use converted kW value
        } else if (rentPriceSubdiv === COST_BASIS_TYPES.PER_KW && targetSubdiv === COST_BASIS_TYPES.PER_MW) {
            unitsToUse = unitsInKw / 1000; // Convert kW to MW
        } else if (rentPriceSubdiv === COST_BASIS_TYPES.PER_MW && targetSubdiv === COST_BASIS_TYPES.PER_MW) {
            unitsToUse = units ?? 0; // Use original MW value
        } else if (rentPriceSubdiv === COST_BASIS_TYPES.PER_KW && targetSubdiv === COST_BASIS_TYPES.PER_KW) {
            unitsToUse = units ?? 0; // Use original kW value
        }

        return (amount ?? 0) * unitsToUse * defaultMultiplier;
    };

    const rentTimeMultiplier = values.rentPriceTime === UNIT_OF_TIME_TYPES.MONTHLY ? 12 : 1;
    const expensesTimeMultiplier = values.expenseCostTime === UNIT_OF_TIME_TYPES.MONTHLY ? 12 : 1;

    const isApartmentWithRetail = Number(values.propertyType.value) === ProFormaModelIds.APARTMENT_WITH_RETAIL;
    const grossSquareFootage = values.grossSquareFootage ?? 0;

    const netRentable = grossSquareFootage * ((values.efficiency ?? 0) / 100);
    const sfPerUnit = values.units ? netRentable / values.units : 0;
    const sfPerBed = totalBeds > 0 ? netRentable / totalBeds : 0;

    const grossPotentialRent = calculateCostWithUnitConversion(
        values.rentPrice,
        values.rentPriceSubdiv,
        values.rentPriceSubdiv,
        values.units,
        rentTimeMultiplier,
        netRentable,
    );

    const primaryExpenseTotal = values.expenseCalculationMode === EXPENSE_CALCULATION_MODE.PERCENT_OF_GPR
        ? grossPotentialRent * ((values.expensePercentOfGpr ?? 0) / 100)
        : calculateCostWithUnitConversion(
            values.expenses,
            values.expenseCostSubdiv,
            values.rentPriceSubdiv,
            values.units,
            expensesTimeMultiplier,
            netRentable,
        );
    
    // Retail calculations
    const retailNetRentable = (values.retailGrossSquareFootage ?? 0) * ((values.retailEfficiency ?? 0) / 100);
    const retailSfPerSuite = (values.retailNumberOfSuites ?? 0) > 0 ? retailNetRentable / (values.retailNumberOfSuites ?? 1) : 0;
    const retailRentTimeMultiplier = values.retailRentPriceTime === UNIT_OF_TIME_TYPES.MONTHLY ? 12 : 1;
    const retailExpenseTimeMultiplier = values.retailExpenseCostTime === UNIT_OF_TIME_TYPES.MONTHLY ? 12 : 1;
    const retailGrossPotentialRent = (values.retailRentPrice ?? 0) * retailNetRentable * retailRentTimeMultiplier;
    const retailExpenseTotal = values.retailExpenseCalculationMode === EXPENSE_CALCULATION_MODE.PERCENT_OF_GPR
        ? retailGrossPotentialRent * ((values.retailExpensePercentOfGpr ?? 0) / 100)
        : (values.retailExpenses ?? 0) * retailNetRentable * retailExpenseTimeMultiplier;

    // Aggregate if apartment with retail
    const totalNetRentable = isApartmentWithRetail ? netRentable + retailNetRentable : netRentable;
    const aggregatedGrossPotentialRent = isApartmentWithRetail ? grossPotentialRent + retailGrossPotentialRent : grossPotentialRent;
    const aggregatedExpenseTotal = isApartmentWithRetail ? primaryExpenseTotal + retailExpenseTotal : primaryExpenseTotal;

    const netOperatingIncome = aggregatedGrossPotentialRent - aggregatedExpenseTotal;
    const exitValuation = values.exitCapRate && values.exitCapRate !== 0
        ? netOperatingIncome / ((values.exitCapRate ?? 1) / 100)
        : 0;

    const calculateDevCost = (
        amount: number | null,
        subdiv: string,
        units: number | null,
        grossSqFt: number | null,
    ) => {
        if (subdiv === COST_BASIS_TYPES.TOTAL) return amount ?? 0;
        if (!isPerUnitType(subdiv)) return (amount ?? 0) * (grossSqFt ?? 0);

        // Handle unit conversions for development costs
        let unitsToUse = units ?? 0;

        if (subdiv === COST_BASIS_TYPES.PER_BED) {
            unitsToUse = totalBeds;
        } else if (values.rentPriceSubdiv === COST_BASIS_TYPES.PER_MW && subdiv === COST_BASIS_TYPES.PER_KW) {
            unitsToUse = unitsInKw; // Use converted kW value
        } else if (values.rentPriceSubdiv === COST_BASIS_TYPES.PER_KW && subdiv === COST_BASIS_TYPES.PER_MW) {
            unitsToUse = unitsInKw / 1000; // Convert kW to MW
        } else if (values.rentPriceSubdiv === COST_BASIS_TYPES.PER_MW && subdiv === COST_BASIS_TYPES.PER_MW) {
            unitsToUse = units ?? 0; // Use original MW value
        } else if (values.rentPriceSubdiv === COST_BASIS_TYPES.PER_KW && subdiv === COST_BASIS_TYPES.PER_KW) {
            unitsToUse = units ?? 0; // Use original kW value
        }

        return (amount ?? 0) * unitsToUse;
    };
    const hardCostsTotal = calculateDevCost(
        values.hardCosts,
        values.hardCostsSubdiv,
        values.units,
        values.grossSquareFootage,
    );

    const softCostsTotal = calculateDevCost(
        values.softCosts,
        values.softCostsSubdiv,
        values.units,
        values.grossSquareFootage,
    );

    const calculateLoanAmount = (totalDevelopmentCosts: number) => {
        const constructionFinancing = values.contructionFinancing ?? 0;
        const marketCapRate = values.marketCapRate ?? 0;

        switch (values.selectedLoanCalculationOptions?.value) {
            case DEVELOPMENT_SOURCE_METRIC.LTV:
                return marketCapRate === 0 ? 0 : (netOperatingIncome / (marketCapRate / 100)) * (constructionFinancing / 100);

            case DEVELOPMENT_SOURCE_METRIC.LTC:
                return (constructionFinancing / 100) * totalDevelopmentCosts;
            case DEVELOPMENT_SOURCE_METRIC.DEBT_YIELD:
                return constructionFinancing === 0 ? 0 : netOperatingIncome / (constructionFinancing / 100);

            default:
                return 0;
        }
    };

    return {
        netRentable,
        totalNetRentable,
        sfPerUnit,
        totalBeds,
        sfPerBed,
        calculateLoanAmount,
        hardCostsTotal,
        softCostsTotal,
        retailSfPerSuite,
        retailExpenseTotal,
        grossPotentialRent: aggregatedGrossPotentialRent,
        expenseTotal: aggregatedExpenseTotal,
        netOperatingIncome: netOperatingIncome,
        exitValuation: exitValuation,
    };
        
};

export const calculateIterativeValues = (
    values: BOESlideoutFormValues,
    hardCostsTotal: number,
    softCostsTotal: number,
    netOperatingIncome: number,
    exitValuation: number,
    calculateLoanAmount: (totalDevelopmentCosts: number) => number,
) => {
    const maxIterations = 100;
    const convergenceThreshold = 0.01;
    let iterations = 0;

    let developerFeeTotal = 0;
    let prevDeveloperFeeTotal = 0;

    let interestReserveTotal = 0;
    let loanAmount = 0;
    let equityCost = 0;
    let returnOnCost = 0;
    let returnOnEquity = 0;
    let profitReturn = 0;
    let totalDevelopmentCosts = 0;

    while (iterations < maxIterations) {
        totalDevelopmentCosts = (values.acquisitionCosts ?? 0) + hardCostsTotal + softCostsTotal + developerFeeTotal + interestReserveTotal;
        developerFeeTotal = ((values.developerFee ?? 0) / 100) * totalDevelopmentCosts;

        loanAmount = calculateLoanAmount(totalDevelopmentCosts);

        const normalizedLoanTerm = values.loanTermTime === UNIT_OF_TIME_TYPES.MONTHLY ? (values.loanTerm ?? 0) / 12 : (values.loanTerm ?? 0);

        interestReserveTotal = loanAmount * ((values.apr ?? 0) / 100) * normalizedLoanTerm * ((values.avgLoanBalance ?? 0) / 100);

        equityCost = totalDevelopmentCosts - loanAmount;

        returnOnCost = totalDevelopmentCosts !== 0 ? netOperatingIncome / totalDevelopmentCosts : 0;

        profitReturn = exitValuation - totalDevelopmentCosts;

        returnOnEquity = equityCost !== 0 ? profitReturn / equityCost : 0;

        if (Math.abs(developerFeeTotal - prevDeveloperFeeTotal) < convergenceThreshold) {
            break;
        }
        prevDeveloperFeeTotal = developerFeeTotal;
        iterations++;
    }

    return {
        developerFeeTotal,
        interestReserveTotal,
        loanAmount,
        equityCost,
        returnOnCost,
        returnOnEquity,
        profitReturn,
        totalDevelopmentCosts,
    };
};

export const getPropertySizeValues = (
    proFormaModelId: ProFormaModelIds,
    propertySizeView: IOptionValue<PropertySizeView>,
    boeScenario: BOEScenario
) => {
    if (!isApartmentWithRetailModel(proFormaModelId) || propertySizeView.value === 'apartment') {
        return {
            builtSf: Number(boeScenario.builtSf || 0),
            efficiency: boeScenario.buildingEfficiency ? Number(boeScenario.buildingEfficiency) : null,
            netRentableSf: boeScenario.netRentableSf ? Number(boeScenario.netRentableSf) : null,
        };
    }
    return {
        builtSf: Number(boeScenario.retailBuiltSf || 0),
        efficiency: boeScenario.retailBuildingEfficiency ? Number(boeScenario.retailBuildingEfficiency) : null,
        netRentableSf: boeScenario.retailNetRentableSf ? Number(boeScenario.retailNetRentableSf) : null,
    };
};

export const getGrossPotentialRentValues = (
    proFormaModelId: ProFormaModelIds,
    gprView: IOptionValue<PropertySizeView>,
    boeScenario: BOEScenario
) => {
    if (!isApartmentWithRetailModel(proFormaModelId) || gprView.value === BOE_PROPERTY_TYPES.APARTMENT) {
        return {
            grossPotentialRent: boeScenario.grossPotentialRent ? Number(boeScenario.grossPotentialRent) : 0,
            monthlyRentPricePerUnit: boeScenario.monthlyRentPricePerUnit ? Number(boeScenario.monthlyRentPricePerUnit) : null,
            monthlyRentPricePerSf: boeScenario.monthlyRentPricePerSf ? Number(boeScenario.monthlyRentPricePerSf) : null,
            numberOfUnits: boeScenario.numberOfUnits ?? null,
            sfPerUnit: boeScenario.sfPerUnit ? Number(boeScenario.sfPerUnit) : null,
        };
    }
    return {
        grossPotentialRent: boeScenario.retailGrossPotentialRent ? Number(boeScenario.retailGrossPotentialRent) : 0,
        monthlyRentPricePerUnit: null,
        monthlyRentPricePerSf: boeScenario.retailRentPricePerSf ? Number(boeScenario.retailRentPricePerSf) : null,
        numberOfUnits: boeScenario.retailNumberOfSuites ?? null,
        sfPerUnit: boeScenario.retailSfPerSuite ? Number(boeScenario.retailSfPerSuite) : null,
    };
};

export const getNetOperatingIncomeValues = (
    proFormaModelId: ProFormaModelIds,
    noiView: IOptionValue<PropertySizeView>,
    boeScenario: BOEScenario
) => {
    const netOperatingIncome = boeScenario.netOperatingIncome ? Number(boeScenario.netOperatingIncome) : 0;

    if (!isApartmentWithRetailModel(proFormaModelId) || noiView.value === 'apartment') {
        return {
            netOperatingIncome,
            monthlyExpensePerUnit: boeScenario.monthlyExpenseCostPerUnit ? Number(boeScenario.monthlyExpenseCostPerUnit) : null,
            monthlyExpensePerSf: boeScenario.monthlyExpenseCostPerSf ? Number(boeScenario.monthlyExpenseCostPerSf) : null,
            totalExpenses: boeScenario.primaryExpensesTotal ? Number(boeScenario.primaryExpensesTotal) : null,
        };
    }
    return {
        netOperatingIncome,
        monthlyExpensePerUnit: null,
        monthlyExpensePerSf: boeScenario.retailExpenseCostPerSf ? Number(boeScenario.retailExpenseCostPerSf) : null,
        totalExpenses: boeScenario.retailExpensesTotal ? Number(boeScenario.retailExpensesTotal) : null,
    };
};

export const getDisplayValue = (value: number | null | undefined, placeholder: string = 'TBD'): string => {
    if (value === 0 || Number.isNaN(value)) {
        return placeholder;
    }
    return numeralFormatterCurrency(value ?? 0);
};

export const getRelevantCostBasisOptions = (
    isDataCenter: boolean,
    isStudentHousing: boolean,
    isApartment: boolean,
    isApartmentWithRetail: boolean,
) => {
    if (isDataCenter) return DATA_CENTER_COST_BASIS_OPTIONS;
    if (isStudentHousing) return STUDENT_HOUSING_COST_BASIS_OPTIONS;
    if (isApartment || isApartmentWithRetail) return COST_BASIS_OPTIONS;
    return COST_BASIS_OPTIONS.filter(option => option?.value !== COST_BASIS_TYPES.PER_UNIT);
};

export const getRelevantHardSoftCostsOptions = (
    isDataCenter: boolean,
    isStudentHousing: boolean,
    isApartment: boolean,
    isApartmentWithRetail: boolean,
) => {
    if (isDataCenter) return DATA_CENTER_HARD_SOFT_COSTS_OPTIONS;
    if (isStudentHousing) return STUDENT_HOUSING_HARD_SOFT_COSTS_OPTIONS;
    if (isApartment || isApartmentWithRetail) return HARD_SOFT_COSTS_OPTIONS;
    return HARD_SOFT_COSTS_OPTIONS.filter(option => option?.value !== COST_BASIS_TYPES.PER_UNIT);
};

export const getRetailCostBasisOptions = () => COST_BASIS_OPTIONS.filter(option => option?.value === COST_BASIS_TYPES.PER_SF);

export type PropertyTypeDefaults = {
    rentPriceSubdiv: BOESlideoutFormValues['rentPriceSubdiv'];
    expenseCostSubdiv: BOESlideoutFormValues['expenseCostSubdiv'];
    softCostsSubdiv: BOESlideoutFormValues['softCostsSubdiv'];
    hardCostsSubdiv: BOESlideoutFormValues['hardCostsSubdiv'];
    resetBedsPerUnit?: boolean;
};

export const getDefaultSubdivsForPropertyType = (propertyTypeId: ProFormaModelIds): PropertyTypeDefaults => {
    if (propertyTypeId === ProFormaModelIds.DATA_CENTER) {
        return {
            rentPriceSubdiv: 'kw', expenseCostSubdiv: 'kw', softCostsSubdiv: 'kw', hardCostsSubdiv: 'kw',
        };
    }
    if (isStudentHousingModel(propertyTypeId)) {
        return {
            rentPriceSubdiv: 'bed',
            expenseCostSubdiv: 'bed',
            softCostsSubdiv: 'bed',
            hardCostsSubdiv: 'bed',
            resetBedsPerUnit: true,
        };
    }
    if (
        propertyTypeId === ProFormaModelIds.APARTMENT
        || isApartmentWithRetailModel(propertyTypeId)
    ) {
        return {
            rentPriceSubdiv: 'unit', expenseCostSubdiv: 'unit', softCostsSubdiv: 'sf', hardCostsSubdiv: 'sf',
        };
    }
    return {
        rentPriceSubdiv: 'sf', expenseCostSubdiv: 'sf', softCostsSubdiv: 'sf', hardCostsSubdiv: 'sf',
    };
};